import { getCurrentScope, onScopeDispose, ref, watch, type Ref } from 'vue';
import {
  GENERATED_RECORD_LOCK_CHANGED_EVENT,
  isGeneratedRecordLockEvent,
  listGeneratedRecords,
} from '@/entities/GeneratedRecord';
import { onRealtimeEvent } from '@/shared/api';
import { useCustomToast, type SortSelection } from '@/shared/lib';
import { GENERATED_RECORD_FILTER_APPLY_ERROR } from '@/pages/home/config';
import {
  toGeneratedRecordFilterParams,
  type GeneratedRecordFilters,
} from './generated-record-filters';
import { NO_GENERATED_RECORD_SORT, toGeneratedRecordListSort } from './generated-record-sort';
import { toQualityRecordRow } from './generated-record-row.mapper';
import type { QualityRecordRow } from './quality-record-row.types';
import {
  DEFAULT_QUALITY_RECORD_SEARCH_FIELD,
  type QualityRecordSearchField,
} from './quality-records-const';

export const DEFAULT_GENERATED_RECORDS_PER_PAGE = 10;

export function useMatrixGeneratedRecordsTab(
  prefix: Ref<string>,
  initialSort: SortSelection = NO_GENERATED_RECORD_SORT,
  matrixId: number | null = null,
  initialFilter: GeneratedRecordFilters = {}
) {
  const documents = ref<QualityRecordRow[]>([]);
  const totalItems = ref(0);
  const currentPage = ref(1);
  const itemsPerPage = ref(DEFAULT_GENERATED_RECORDS_PER_PAGE);
  const loading = ref(false);
  const error = ref(false);
  const searchTerm = ref('');
  const searchFieldId = ref<QualityRecordSearchField>(DEFAULT_QUALITY_RECORD_SEARCH_FIELD);
  const currentFilter = ref<GeneratedRecordFilters>({ ...initialFilter });
  const { showErrorToast } = useCustomToast();
  const currentSort = ref<SortSelection>({ ...initialSort });
  let latestRequest = 0;

  function buildParams(filters: GeneratedRecordFilters, page: number) {
    const sort = toGeneratedRecordListSort(currentSort.value);

    return {
      prefix: prefix.value,
      ...toGeneratedRecordFilterParams(filters),
      ...(sort ? { sort } : {}),
      page,
      limit: itemsPerPage.value,
      ...(matrixId !== null && { matrixId }),
    };
  }

  // Only the newest request may publish: a slow page must not overwrite the one asked after it.
  // A silent fetch follows a change somebody else made: no spinner, and a failure keeps the rows.
  async function fetch({ silent = false }: { silent?: boolean } = {}): Promise<void> {
    const request = ++latestRequest;

    if (!silent) {
      loading.value = true;
      error.value = false;
    }

    try {
      const result = await listGeneratedRecords(buildParams(currentFilter.value, currentPage.value));
      if (request !== latestRequest) return;
      documents.value = result.data.map(toQualityRecordRow);
      totalItems.value = result.metadata.total;
    } catch {
      if (request !== latestRequest || silent) return;
      documents.value = [];
      totalItems.value = 0;
      error.value = true;
    } finally {
      if (request === latestRequest) {
        loading.value = false;
      }
    }
  }

  const handlePageChange = (page: number) => {
    currentPage.value = page;
    return fetch();
  };

  const handleQuantityChange = (quantity: number) => {
    itemsPerPage.value = quantity;
    currentPage.value = 1;
    return fetch();
  };

  // The filter is committed only once the list answers: a failure leaves the rows, the page and the
  // active state of the button as they were.
  async function handleFilter(filters: GeneratedRecordFilters): Promise<void> {
    const request = ++latestRequest;
    const candidate = { ...filters };
    loading.value = true;

    try {
      const result = await listGeneratedRecords(buildParams(candidate, 1));
      if (request !== latestRequest) return;
      currentFilter.value = candidate;
      currentPage.value = 1;
      error.value = false;
      documents.value = result.data.map(toQualityRecordRow);
      totalItems.value = result.metadata.total;
    } catch {
      if (request !== latestRequest) return;
      showErrorToast({ ...GENERATED_RECORD_FILTER_APPLY_ERROR, type: 'error' });
    } finally {
      if (request === latestRequest) {
        loading.value = false;
      }
    }
  }

  const handleSort = (sort: string, order: string) => {
    currentSort.value = { sort, order };
    currentPage.value = 1;
    return fetch();
  };

  // The route reuses this component for another matrix, and a stage of one lineage means nothing
  // in another, so neither the filter nor the sort travels along. The first run keeps the sort the
  // page read from the URL.
  watch(
    prefix,
    (_, previousPrefix) => {
      if (previousPrefix !== undefined) {
        currentFilter.value = {};
        currentSort.value = { ...NO_GENERATED_RECORD_SORT };
      }
      currentPage.value = 1;
      void fetch();
    },
    { immediate: true }
  );

  // The event carries only the id: the rows come back through the list route, which applies the
  // directory scope, so the socket never tells anyone more than that a record changed.
  const stopLockEvents = onRealtimeEvent(GENERATED_RECORD_LOCK_CHANGED_EVENT, (event: unknown) => {
    if (!isGeneratedRecordLockEvent(event)) return;
    if (documents.value.some((row) => row.id === event.generatedRecordId)) {
      void fetch({ silent: true });
    }
  });
  if (getCurrentScope()) onScopeDispose(stopLockEvents);

  return {
    documents,
    totalItems,
    currentPage,
    itemsPerPage,
    loading,
    error,
    searchTerm,
    searchFieldId,
    currentFilter,
    currentSort,
    retry: () => fetch(),
    refresh: () => fetch({ silent: true }),
    handlePageChange,
    handleQuantityChange,
    handleFilter,
    handleSort,
  };
}
